import React from 'react';
import { Info, AlertTriangle, Lightbulb, ShieldAlert, Monitor, Globe } from 'lucide-react';
import {
  CHANGE_KIND_LABEL,
  HELP_CHANGELOG,
  RECENT_BADGE_DAYS,
  kstDateKey,
  recentAnchorBadges,
  shortDate,
} from './helpChangelog';
import type { AnchorBadge, HelpChangeKind } from './helpChangelog';

/** 키보드 키 표기: <Kbd>Ctrl</Kbd> + <Kbd>F</Kbd> */
export const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="inline-flex items-center px-1.5 py-0.5 mx-0.5 text-[11px] font-semibold font-sans text-slate-700 bg-slate-100 border border-slate-300 border-b-2 rounded shadow-sm whitespace-nowrap">
    {children}
  </kbd>
);

/** 화면 안의 버튼/메뉴 이름 강조: 탭 바 <UI>프로필 설정</UI> */
export const UI: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center px-1.5 py-0.5 mx-0.5 text-[12px] font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded whitespace-nowrap">
    {children}
  </span>
);

/** 메뉴 경로: <Path items={['탭 바 내 사진', '프로필 설정', '데스크톱 설정']} /> */
export const Path: React.FC<{ items: string[] }> = ({ items }) => (
  <span className="inline-flex flex-wrap items-center gap-1 align-middle">
    {items.map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span className="text-slate-400 text-xs">→</span>}
        <UI>{item}</UI>
      </React.Fragment>
    ))}
  </span>
);

type Tone = 'info' | 'warn' | 'danger' | 'tip';

const TONE_STYLE: Record<Tone, { box: string; icon: string; Icon: typeof Info; label: string }> = {
  info: { box: 'bg-blue-50 border-blue-200', icon: 'text-blue-600', Icon: Info, label: '참고' },
  tip: { box: 'bg-emerald-50 border-emerald-200', icon: 'text-emerald-600', Icon: Lightbulb, label: '도움말' },
  warn: { box: 'bg-amber-50 border-amber-200', icon: 'text-amber-600', Icon: AlertTriangle, label: '주의' },
  danger: { box: 'bg-brand-50 border-brand-200', icon: 'text-brand-600', Icon: ShieldAlert, label: '꼭 확인하세요' },
};

/** 강조 박스 */
export const Callout: React.FC<{ tone?: Tone; title?: string; children: React.ReactNode }> = ({
  tone = 'info',
  title,
  children,
}) => {
  const s = TONE_STYLE[tone];
  return (
    <div className={`flex gap-3 p-4 my-4 border rounded-lg ${s.box} break-inside-avoid`}>
      <s.Icon className={`h-5 w-5 shrink-0 mt-0.5 ${s.icon}`} />
      <div className="min-w-0 text-sm text-slate-700 leading-relaxed space-y-1.5">
        <p className="font-semibold text-slate-800">{title || s.label}</p>
        <div className="space-y-1.5">{children}</div>
      </div>
    </div>
  );
};

/** 환경 배지 */
export const Only: React.FC<{ env: 'desktop' | 'web' }> = ({ env }) => {
  const isDesktop = env === 'desktop';
  const Icon = isDesktop ? Monitor : Globe;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 ml-1.5 align-middle text-[11px] font-medium rounded border ${
        isDesktop
          ? 'text-indigo-700 bg-indigo-50 border-indigo-200'
          : 'text-teal-700 bg-teal-50 border-teal-200'
      }`}
    >
      <Icon className="h-3 w-3" />
      {isDesktop ? 'PC 앱 전용' : '웹 전용'}
    </span>
  );
};

/** 번호가 매겨진 조작 순서 */
export const Steps: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ol className="my-4 space-y-2.5">
    {React.Children.toArray(children)
      .filter(Boolean)
      .map((child, i) => (
        <li key={i} className="flex gap-3">
          <span className="shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-brand-600 text-white text-xs font-bold">
            {i + 1}
          </span>
          <div className="pt-0.5 text-sm text-slate-700 leading-relaxed min-w-0">{child}</div>
        </li>
      ))}
  </ol>
);

/** 표 (좁은 화면에서는 가로 스크롤) */
export const Table: React.FC<{ head: string[]; rows: React.ReactNode[][] }> = ({ head, rows }) => (
  <div className="my-4 -mx-1 overflow-x-auto">
    <table className="w-full min-w-[420px] text-sm border-collapse">
      <thead>
        <tr className="bg-slate-100">
          {head.map((h, i) => (
            <th
              key={i}
              className="px-3 py-2 text-left font-semibold text-slate-700 border border-slate-200 whitespace-nowrap"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="even:bg-slate-50/60 align-top">
            {row.map((cell, j) => (
              <td key={j} className="px-3 py-2 text-slate-700 border border-slate-200 leading-relaxed">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** 업데이트 종류 칩 색 — 브랜드 레드는 쓰지 않는다(액션·경고로 읽힌다). 클래스는 통째로 적는다(JIT 스캔) */
export const CHANGE_KIND_CHIP: Record<HelpChangeKind, string> = {
  new: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  changed: 'text-slate-600 bg-slate-100 border-slate-200',
  fixed: 'text-blue-700 bg-blue-50 border-blue-200',
};

/** `<Sub anchor="calls-invite">`의 DOM id — 장 id(`help-calls`)와 겹치지 않게 접두를 따로 둔다 */
export const helpSubDomId = (anchor: string) => `help-sub-${anchor}`;

/** 칩 계산은 날짜가 바뀔 때만 다시 한다(소제목 100여 개가 매번 내역을 훑지 않게) */
let badgeCache: { key: string; map: Record<string, AnchorBadge> } | null = null;
function recentBadge(anchor: string): AnchorBadge | undefined {
  const key = kstDateKey(Date.now());
  if (!badgeCache || badgeCache.key !== key) {
    badgeCache = { key, map: recentAnchorBadges(HELP_CHANGELOG, key, RECENT_BADGE_DAYS) };
  }
  return badgeCache.map[anchor];
}

/**
 * 소제목. `anchor`를 주면 업데이트 내역(15장)에서 이 소제목으로 이동할 수 있고, 최근 바뀐 소제목이면
 * 제목 뒤에 '새 기능 11.04' 칩이 붙는다. ⚠ 칩의 `data-help-meta`는 설명서 검색이 그 글자를 읽지 않게
 * 하는 계약이다(useHelpSearch) — 빼면 결과 제목이 '통화 채팅새 기능11.04'가 된다.
 */
export const Sub: React.FC<{ anchor?: string; children: React.ReactNode }> = ({ anchor, children }) => {
  const badge = anchor ? recentBadge(anchor) : undefined;
  return (
    <h3
      id={anchor ? helpSubDomId(anchor) : undefined}
      className="mt-8 mb-3 text-base font-bold text-slate-800 pb-1.5 border-b border-slate-200 scroll-mt-6"
    >
      {children}
      {badge && (
        <span
          data-help-meta=""
          className={`inline-flex items-center px-1.5 py-0.5 ml-1.5 align-middle text-[11px] font-medium rounded border whitespace-nowrap ${CHANGE_KIND_CHIP[badge.kind]}`}
        >
          {CHANGE_KIND_LABEL[badge.kind]} {shortDate(badge.date)}
        </span>
      )}
    </h3>
  );
};

/** 본문 문단 */
export const P: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <p className={`my-3 text-sm text-slate-700 leading-relaxed ${className || ''}`}>{children}</p>
);

/** 글머리 목록 */
export const List: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul className="my-3 space-y-1.5 text-sm text-slate-700 leading-relaxed">
    {React.Children.toArray(children)
      .filter(Boolean)
      .map((child, i) => (
        <li key={i} className="flex gap-2">
          <span className="shrink-0 text-brand-600 mt-[1px]">•</span>
          <div className="min-w-0">{child}</div>
        </li>
      ))}
  </ul>
);

/** 자주 묻는 질문 항목. data-help-block: 설명서 검색이 질문 하나를 결과 하나로 묶는 경계(useHelpSearch) */
export const Faq: React.FC<{ q: string; children: React.ReactNode }> = ({ q, children }) => (
  <div
    data-help-block=""
    data-help-title={q}
    className="my-4 p-4 border border-slate-200 rounded-lg bg-white break-inside-avoid"
  >
    <p className="flex gap-2 font-semibold text-slate-800 text-sm">
      <span className="shrink-0 text-brand-600">Q.</span>
      <span>{q}</span>
    </p>
    <div className="mt-2 pl-6 text-sm text-slate-600 leading-relaxed space-y-2">{children}</div>
  </div>
);
